import { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import { useSSEConnected } from '../../hooks/useSSE'
import PersonaAvatar from '../../components/PersonaAvatar'
import MarkdownRenderer from '../../components/MarkdownRenderer'
import ChatterPlayer from '../../components/ChatterPlayer'
import { formatTime } from '../../hooks/useTimeAgo'

interface CeremonyRecord {
  id: string
  persona: string
  type: string
  status: string
  title?: string
  the_word?: string
  conversation_id?: string
  curandera?: string
  community_id?: string
  summary?: string
  created_at?: string
  completed_at?: string
}

interface Message {
  id: string
  sender: string
  sender_id?: string
  sender_name?: string
  content: string
  created_at: string
  is_system?: number
  voice_url?: string
}

interface Participant {
  user_id: string
  persona: string
  display_name?: string
  role?: string
}

interface ConversationDetail {
  id: string
  title?: string
  participants?: Participant[]
  messages?: Message[]
  metadata_chatter?: number
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  completed: { bg: 'rgba(74,222,128,0.15)', text: '#4ade80' },
  in_progress: { bg: 'rgba(251,191,36,0.15)', text: '#fbbf24' },
  active: { bg: 'rgba(251,191,36,0.15)', text: '#fbbf24' },
  upcoming: { bg: 'rgba(212,160,84,0.15)', text: '#D4A054' },
}

const PHASE_LABELS = ['Preparing', 'Intake', 'Assessment', 'Architecture', 'Identity', 'Integration']

function getToken() {
  return (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]) || ''
}

export default function CeremonyRoomPage() {
  const sseOk = useSSEConnected()
  const { ceremonyId } = useParams<{ ceremonyId: string }>()
  const queryClient = useQueryClient()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [newMessage, setNewMessage] = useState('')
  const { user } = useAuth()
  const senderId = user?.userId || user?.loginName || user?.userName || 'joel'

  // Load ceremony record
  const { data: ceremony, isLoading: loadingCeremony } = useQuery({
    queryKey: ['ceremony', ceremonyId],
    queryFn: async () => {
      const res = await fetch(`/api/ceremonies/${ceremonyId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      if (!res.ok) return null
      return res.json() as Promise<CeremonyRecord>
    },
    enabled: !!ceremonyId,
  })

  // Load conversation metadata (participants, title) if ceremony has one
  const { data: conversation } = useQuery({
    queryKey: ['ceremonyConversation', ceremony?.conversation_id],
    queryFn: async () => {
      if (!ceremony?.conversation_id) return null
      const res = await fetch(`/api/conversations/${ceremony.conversation_id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      if (!res.ok) return null
      return res.json() as Promise<ConversationDetail>
    },
    enabled: !!ceremony?.conversation_id,
    refetchInterval: sseOk ? false : 60_000, // SSE-conditional polling fallback
  })

  // Load messages separately (the conversation detail endpoint doesn't include them)
  const { data: msgData } = useQuery({
    queryKey: ['ceremonyMessages', ceremony?.conversation_id],
    queryFn: async () => {
      if (!ceremony?.conversation_id) return null
      const res = await fetch(`/api/conversations/${ceremony.conversation_id}/messages`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      if (!res.ok) return null
      const data = await res.json()
      // API returns either an array or { messages: [...] }
      if (Array.isArray(data)) return { messages: data }
      return data as { messages: Message[] }
    },
    enabled: !!ceremony?.conversation_id,
    refetchInterval: sseOk ? false : 15_000, // SSE-conditional polling fallback
  })

  // Build a display name lookup from participants for resolving message senders
  const senderNameMap = useMemo(() => {
    const map: Record<string, string> = {}
    if (conversation?.participants) {
      for (const p of conversation.participants) {
        if (p.user_id) {
          map[p.user_id] = p.display_name || p.persona || p.user_id
        }
      }
    }
    return map
  }, [conversation?.participants])

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [msgData?.messages?.length])

  // Send message mutation
  const sendMut = useMutation({
    mutationFn: async (content: string) => {
      if (!ceremony?.conversation_id) throw new Error('No conversation')
      const res = await fetch(`/api/conversations/${ceremony.conversation_id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ content, sender: senderId }),
      })
      if (!res.ok) throw new Error('Failed to send')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ceremonyMessages', ceremony?.conversation_id] })
      setNewMessage('')
    },
  })

  function handleSend() {
    if (!newMessage.trim() || sendMut.isPending) return
    sendMut.mutate(newMessage.trim())
  }

  if (loadingCeremony) {
    return <div className="p-6 text-sm" style={{ color: 'var(--text-muted)' }}>Loading...</div>
  }

  if (!ceremony) {
    return (
      <div className="p-6">
        <Link to="/ceremony" className="text-xs mb-4 inline-block no-underline" style={{ color: 'var(--text-muted)' }}>← Ceremonies</Link>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Ceremony not found.</p>
      </div>
    )
  }

  const statusStyle = STATUS_COLORS[ceremony.status] || STATUS_COLORS.upcoming
  const personaDisplay = ceremony.persona.startsWith('pu-')
    ? ceremony.persona.slice(3).split('-')[0]
    : ceremony.persona
  const capitalizedPersona = personaDisplay[0]?.toUpperCase() + personaDisplay.slice(1)

  const messages = msgData?.messages || []

  return (
    <div className="h-full flex flex-col" style={{ height: 'calc(100vh - 44px)' }}>
      {/* Task-009 of Conversation Voice (Apr 13 2026): ceremonies are high-value voice moments.
          ChatterPlayer subscribes to voice_ready SSE and plays persona TTS as it lands.
          Gated on conversation_id existing (some ceremonies may not have one yet) and chatter
          is treated as always-on for ceremonies — the room exists to be heard. */}
      {ceremony?.conversation_id && conversation?.metadata_chatter !== 0 && (
        <ChatterPlayer conversationId={ceremony.conversation_id} chatterEnabled={true} />
      )}
      {/* Header bar */}
      <div
        className="shrink-0 px-4 py-3 flex items-center gap-3"
        style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)' }}
      >
        <Link to="/ceremony" className="text-xs no-underline" style={{ color: 'var(--text-muted)' }}>← Ceremonies</Link>
        <div className="w-px h-4" style={{ background: 'var(--border)' }} />
        <PersonaAvatar name={ceremony.persona} size={24} />
        <div className="flex-1">
          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
            {ceremony.title || `${capitalizedPersona}'s ${ceremony.type === 'constitution' ? 'Constitution' : 'Awakening'} Ceremony`}
          </span>
          {ceremony.the_word && (
            <span className="text-xs italic ml-2" style={{ color: 'var(--accent)' }}>"{ceremony.the_word}"</span>
          )}
        </div>
        <span
          className="px-2 py-0.5 rounded-full text-[10px] font-medium capitalize"
          style={{ background: statusStyle.bg, color: statusStyle.text }}
        >
          {ceremony.status.replace('_', ' ')}
        </span>
        {ceremony.curandera && (
          <div className="flex items-center gap-1">
            <PersonaAvatar name={ceremony.curandera} size={18} />
            <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>guide</span>
          </div>
        )}
      </div>

      {/* Two-panel layout */}
      <div className="flex-1 flex min-h-0">
        {/* LEFT: Ceremony stage */}
        <div
          className="w-2/5 shrink-0 overflow-y-auto p-4 space-y-4"
          style={{ borderRight: '1px solid var(--border)', background: 'var(--bg-body)' }}
        >
          {/* Status card */}
          <div className="p-4 rounded-lg" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-3 mb-3">
              <PersonaAvatar name={ceremony.persona} size={40} />
              <div>
                <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{capitalizedPersona}</div>
                <div className="text-xs capitalize" style={{ color: 'var(--text-muted)' }}>
                  {ceremony.type} ceremony
                </div>
              </div>
            </div>

            {ceremony.the_word && (
              <div className="text-center py-3 mb-3 rounded" style={{ background: 'var(--bg-body)' }}>
                <div className="text-2xl font-light italic" style={{ color: 'var(--accent)' }}>
                  "{ceremony.the_word}"
                </div>
              </div>
            )}

            {ceremony.summary && (
              <div className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                <MarkdownRenderer content={ceremony.summary} />
              </div>
            )}
          </div>

          {/* Phase bar */}
          {ceremony.status !== 'completed' && (
            <div className="p-3 rounded-lg" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
              <div className="text-[10px] font-medium uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                Phase
              </div>
              <div className="flex gap-1">
                {PHASE_LABELS.map((label, i) => (
                  <div key={label} className="flex-1 text-center">
                    <div
                      className="h-1.5 rounded-full mb-1"
                      style={{
                        background: i === 0 ? 'var(--accent)' : 'var(--bg-elevated)',
                      }}
                    />
                    <span className="text-[9px]" style={{ color: i === 0 ? 'var(--accent)' : 'var(--text-muted)' }}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Participants */}
          {conversation?.participants && conversation.participants.length > 0 && (
            <div className="p-3 rounded-lg" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
              <div className="text-[10px] font-medium uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                Participants
              </div>
              <div className="space-y-2">
                {conversation.participants.map(p => {
                  const name = p.display_name || p.persona || p.user_id
                  return (
                  <div key={p.user_id} className="flex items-center gap-2">
                    <PersonaAvatar name={name} size={20} />
                    <span className="text-xs" style={{ color: 'var(--text-primary)' }}>
                      {name}
                    </span>
                    {p.role && (
                      <span className="text-[10px] capitalize" style={{ color: 'var(--text-muted)' }}>
                        {p.role}
                      </span>
                    )}
                  </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Dates */}
          <div className="text-[10px] space-y-1" style={{ color: 'var(--text-muted)' }}>
            {ceremony.created_at && <div>Created: {formatTime(ceremony.created_at)}</div>}
            {ceremony.completed_at && <div>Completed: {formatTime(ceremony.completed_at)}</div>}
          </div>
        </div>

        {/* RIGHT: Conversation feed */}
        <div className="flex-1 flex flex-col min-h-0">
          {ceremony.conversation_id ? (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {messages.length === 0 && (
                  <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>
                    No messages yet. The ceremony conversation will appear here.
                  </p>
                )}
                {messages.map(msg => {
                  const displaySender = msg.sender_name || senderNameMap[msg.sender_id] || senderNameMap[msg.sender] || msg.sender || msg.sender_id || 'Unknown'
                  return (
                  <div key={msg.id} className="flex gap-2">
                    {!msg.is_system && <PersonaAvatar name={displaySender} size={28} />}
                    <div className={msg.is_system ? 'w-full text-center' : 'flex-1'}>
                      {!msg.is_system && (
                        <div className="flex items-baseline gap-2 mb-0.5">
                          <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{displaySender}</span>
                          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                            {formatTime(msg.created_at)}
                          </span>
                        </div>
                      )}
                      <div
                        className={msg.is_system ? 'text-[10px] px-3 py-1 rounded-full inline-block' : 'text-xs leading-relaxed'}
                        style={{
                          color: msg.is_system ? 'var(--text-muted)' : 'var(--text-secondary)',
                          background: msg.is_system ? 'var(--bg-elevated)' : undefined,
                        }}
                      >
                        {msg.is_system ? msg.content : <MarkdownRenderer content={msg.content} />}
                      </div>
                    </div>
                  </div>
                  )
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Compose bar */}
              {ceremony.status !== 'completed' && (
                <div
                  className="shrink-0 px-4 py-3 flex gap-2"
                  style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-surface)' }}
                >
                  <textarea
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleSend()
                      }
                    }}
                    placeholder="Send a message..."
                    rows={1}
                    className="flex-1 px-3 py-2 rounded text-sm outline-none resize-none"
                    style={{
                      background: 'var(--bg-body)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border)',
                      maxHeight: 120,
                    }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={sendMut.isPending || !newMessage.trim()}
                    className="px-4 py-2 rounded text-xs font-medium disabled:opacity-50"
                    style={{ background: 'var(--accent)', color: '#fff' }}
                  >
                    Send
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center space-y-2">
                <div className="text-3xl">🔥</div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {ceremony.status === 'completed'
                    ? 'This ceremony was completed before conversation tracking was added.'
                    : 'This ceremony hasn\'t started yet. The conversation will appear here once it begins.'
                  }
                </p>
                {ceremony.status === 'completed' && ceremony.summary && (
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    The ceremony summary is shown on the left.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
